<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMetaCampaignsAttributionStore } from 'dashboard/stores/metaCampaignsAttribution';
import { useMetaCampaignsCampaignsStore } from 'dashboard/stores/metaCampaignsCampaigns';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const attributionStore = useMetaCampaignsAttributionStore();
const campaignsStore = useMetaCampaignsCampaignsStore();

const isEditing = ref(false);
const selectedCampaignId = ref('');

const attribution = computed(
  () => attributionStore.byConversation[props.conversationId] || null
);
const isSaving = computed(() => attributionStore.uiFlags.saving);
const campaigns = computed(() => campaignsStore.getCampaigns);

const sourceLabel = computed(() => {
  if (!attribution.value) return '';
  return attribution.value.source === 'manual'
    ? t('META_CAMPAIGNS.ATTRIBUTION.SOURCE_MANUAL')
    : t('META_CAMPAIGNS.ATTRIBUTION.SOURCE_AUTO');
});

const load = async () => {
  await attributionStore.fetch(props.conversationId);
};

const startEditing = async () => {
  if (!campaigns.value.length) await campaignsStore.fetch();
  selectedCampaignId.value = attribution.value?.campaign_id || '';
  isEditing.value = true;
};

const save = async () => {
  if (!selectedCampaignId.value) return;
  try {
    await attributionStore.setCampaign(
      props.conversationId,
      selectedCampaignId.value
    );
    isEditing.value = false;
    useAlert(t('META_CAMPAIGNS.ATTRIBUTION.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(t('META_CAMPAIGNS.ATTRIBUTION.SAVE_ERROR'));
  }
};

const clear = async () => {
  try {
    await attributionStore.remove(props.conversationId);
    isEditing.value = false;
  } catch (error) {
    useAlert(t('META_CAMPAIGNS.ATTRIBUTION.SAVE_ERROR'));
  }
};

onMounted(load);
watch(() => props.conversationId, load);
</script>

<template>
  <div class="flex flex-col gap-2 px-4 py-3">
    <template v-if="!isEditing">
      <div v-if="attribution?.campaign_name" class="flex flex-col gap-1">
        <div class="flex items-center gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ attribution.campaign_name }}
          </span>
          <span
            class="text-xs px-2 py-0.5 rounded-full font-medium bg-n-slate-3 text-n-slate-11"
          >
            {{ sourceLabel }}
          </span>
        </div>
        <span v-if="attribution.headline" class="text-xs text-n-slate-10">
          {{ attribution.headline }}
        </span>
      </div>

      <p
        v-else-if="attribution?.pending_resolution"
        class="text-xs text-n-slate-10"
      >
        {{ t('META_CAMPAIGNS.ATTRIBUTION.PENDING') }}
      </p>

      <p v-else class="text-xs text-n-slate-10">
        {{ t('META_CAMPAIGNS.ATTRIBUTION.NONE') }}
      </p>

      <div class="flex gap-2">
        <Button
          size="sm"
          variant="faded"
          color="slate"
          :label="
            attribution?.campaign_id
              ? t('META_CAMPAIGNS.ATTRIBUTION.CHANGE')
              : t('META_CAMPAIGNS.ATTRIBUTION.SET')
          "
          @click="startEditing"
        />
      </div>
    </template>

    <template v-else>
      <select
        v-model="selectedCampaignId"
        class="w-full rounded-lg border border-n-strong bg-n-alpha-3 px-3 py-2 text-sm text-n-slate-12 focus:outline-none"
      >
        <option value="">
          {{ t('META_CAMPAIGNS.ATTRIBUTION.SELECT_PLACEHOLDER') }}
        </option>
        <option
          v-for="campaign in campaigns"
          :key="campaign.id"
          :value="campaign.id"
        >
          {{ campaign.name }}
        </option>
      </select>
      <div class="flex gap-2 justify-end">
        <Button
          size="sm"
          variant="link"
          color="slate"
          :label="t('META_CAMPAIGNS.ATTRIBUTION.CANCEL')"
          @click="isEditing = false"
        />
        <Button
          v-if="attribution?.campaign_id"
          size="sm"
          variant="faded"
          color="ruby"
          :label="t('META_CAMPAIGNS.ATTRIBUTION.CLEAR')"
          :is-loading="isSaving"
          @click="clear"
        />
        <Button
          size="sm"
          :label="t('META_CAMPAIGNS.ATTRIBUTION.SAVE')"
          :disabled="!selectedCampaignId"
          :is-loading="isSaving"
          @click="save"
        />
      </div>
    </template>
  </div>
</template>
